'use client';

import Script from 'next/script';
import React from 'react';

/* eslint-disable @typescript-eslint/no-explicit-any, prefer-rest-params, @typescript-eslint/no-unused-expressions */

interface ThirdPartyScriptsProps {
  gtmId?: string;
}

// Default GTM ID - update this to match your actual GTM container ID
const DEFAULT_GTM_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID;

interface GeolocationResponse {
  country: string;
  ip: string | null;
  source: string;
  isGDPR: boolean;
}

/**
 * Optimized third-party scripts component implementing Google Consent Mode v2
 * with region-specific default consent behavior:
 * 1. GDPR regions (EU/EEA): Default to 'denied', require explicit consent
 * 2. US and other regions: Default to 'granted', allow opt-out
 * 3. Always show banner for transparency and user control
 * 4. Update consent state when user accepts/rejects
 */
export const ThirdPartyScripts = ({
  gtmId = DEFAULT_GTM_ID,
}: ThirdPartyScriptsProps) => {
  const [isGDPRRegion, setIsGDPRRegion] = React.useState<boolean>(false);
  const [regionLoaded, setRegionLoaded] = React.useState<boolean>(false);

  // Allow brief activation of tracking in development via env or URL param
  const forceTrackingEnv = process.env.NEXT_PUBLIC_FORCE_TRACKING === 'true';
  const [forceTrackingUrl, setForceTrackingUrl] = React.useState(false);
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      try {
        const params = new URLSearchParams(window.location.search);
        const forced =
          params.get('forceTracking') === '1' ||
          params.get('enableTracking') === '1';
        if (forced) setForceTrackingUrl(true);
      } catch {
        // ignore
      }
    }
  }, []);

  // Detect user's region using our production geolocation API
  React.useEffect(() => {
    const detectRegion = async () => {
      try {
        const response = await fetch('/api/geolocation');
        if (response.ok) {
          const data: GeolocationResponse = await response.json();
          setIsGDPRRegion(data.isGDPR);
        } else {
          setIsGDPRRegion(false); // Default to non-GDPR
        }
      } catch {
        setIsGDPRRegion(false);
      } finally {
        setRegionLoaded(true);
      }
    };

    detectRegion();
  }, []);

  // Google Tag always loads in production (with consent mode)
  const isProduction = process.env.NODE_ENV === 'production';
  const prodLike = isProduction || forceTrackingEnv || forceTrackingUrl;
  const shouldLoadGoogleTag = prodLike && gtmId && regionLoaded;

  // All third-party vendors are managed through GTM, no individual scripts needed

  return (
    <>
      {/* Google Consent Mode v2 - Region-specific default consent */}
      {shouldLoadGoogleTag && (
        <Script
          id="gtag-consent-default"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              
              // Set region-specific default consent
              ${
                isGDPRRegion
                  ? `
              // GDPR region: Default to 'denied' - require explicit consent
              gtag('consent', 'default', {
                'ad_storage': 'denied',
                'ad_user_data': 'denied', 
                'ad_personalization': 'denied',
                'analytics_storage': 'denied',
                'region': ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB'],
                'wait_for_update': 500
              });
              `
                  : `
              // Non-GDPR region: Default to 'granted' - allow opt-out
              gtag('consent', 'default', {
                'ad_storage': 'granted',
                'ad_user_data': 'granted',
                'ad_personalization': 'granted', 
                'analytics_storage': 'granted'
              });
              `
              }
              
              // Enable URL passthrough for better measurement without cookies
              gtag('set', 'url_passthrough', true);
              gtag('set', 'ads_data_redaction', true);
            `,
          }}
        />
      )}

      {/* Google Tag Manager - All tracking (including GA4) managed through GTM */}
      {shouldLoadGoogleTag && (
        <>
          <Script
            id="gtm-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtmId}');
              `,
            }}
          />

          {/* GTM NoScript fallback */}
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
        </>
      )}
    </>
  );
};

// Default export for backward compatibility
export default ThirdPartyScripts;
